import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import Pane from 'components/Common/Pane'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import TextButton from 'LuxKit/Button/TextButton'
import Sheet from 'components/utils/Sheet'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getImageUrl } from 'lib/image/imageUtils'

const FLIGHTS_BANNER_IMAGE_ID = 'luxplus-landing-banner-9656ba981c47'
// Same blob-shape corner radii as the LuxPlus+ Month campaign's other circular image overlays
// (LuxPlusMonthTakeoverHomeBanner's StyledCircleOverlay) — kept local rather than imported from
// that feature's constants, since this is a shared component outside the takeover feature.
const BLOB_SHAPE_LARGE_RADIUS_PERCENT = '53.38%'
const BLOB_SHAPE_SMALL_RADIUS_PERCENT = '3.2%'

// Same container padding pattern as the LuxPlus+ Month campaign hero (LuxPlusMonthTakeoverHero) —
// the banner sits flush edge-to-edge on mobile, then insets within a bordered card from desktop up.
const BannerContainer = styled(LayoutContainer)`
  padding-top: 0;
  padding-inline: 0;
  margin-block-end: ${rem(40)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(40)};
    padding-inline: ${rem(16)};
  }
`

const Banner = styled(Sheet)`
  overflow: hidden;

  ${mediaQueryUp.desktop} {
    border-radius: ${rem(16)};
  }
`

// A single row always: title/subtitle/button stack together in the start column, image sits in
// the end column. On mobile the button breaks out of the stack's own column width (see
// ButtonBreakout) to visually span the full banner width below the image, matching the Figma
// mobile reference — from tablet up it just stays a normal stack item beside the image.
const BannerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas: 'title image';
  column-gap: ${rem(16)};
  padding: ${rem(20)};

  ${mediaQueryUp.desktop} {
    column-gap: ${rem(32)};
    height: ${rem(300)};
    padding: 0;
  }
`

const TitleBlock = styled.div`
  grid-area: title;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  align-self: center;

  ${mediaQueryUp.desktop} {
    gap: ${rem(12)};
    padding-inline-start: ${rem(40)};
  }
`

// On mobile only, breaks out of the title column's width to span the full banner width (covering
// the image column + gap too), matching the Figma mobile reference where the button sits full
// width below everything else. From tablet up it's just a normal stack item.
const ButtonBreakout = styled.div`
  width: calc(100% + ${rem(16 + 96)});
  margin-inline-end: -${rem(16 + 96)};

  ${mediaQueryUp.tablet} {
    width: auto;
    margin-inline-end: 0;
  }
`

// On mobile the image is a small blob-shaped circle sitting beside the title (same treatment as
// LuxPlusMonthTakeoverHomeBanner's StyledCircleOverlay). From desktop up it fills the full grid
// cell instead (full banner height).
const ImageWrapper = styled.div`
  position: relative;
  grid-area: image;
  align-self: start;
  width: ${rem(96)};
  height: ${rem(96)};
  overflow: hidden;
  border-start-start-radius: ${BLOB_SHAPE_LARGE_RADIUS_PERCENT};
  border-start-end-radius: ${BLOB_SHAPE_LARGE_RADIUS_PERCENT};
  border-end-start-radius: ${BLOB_SHAPE_LARGE_RADIUS_PERCENT};
  border-end-end-radius: ${BLOB_SHAPE_SMALL_RADIUS_PERCENT};

  ${mediaQueryUp.tablet} {
    width: ${rem(156)};
    height: ${rem(156)};
  }

  ${mediaQueryUp.desktop} {
    align-self: stretch;
    width: ${rem(510)};
    height: ${rem(300)};
    border-radius: 0;
  }
`

// Same technique as LuxPlusMonthTakeoverHero's HeroImageFrame — width is auto, which the CSS spec
// always scales to preserve the photo's own intrinsic aspect ratio, so it can never distort
// regardless of frame shape. background-position/background-size can be tweaked here to zoom or
// re-crop the image, same as the hero.
const ImageFrame = styled.div`
  position: absolute;
  inset: 0;
  background: url(${getImageUrl(FLIGHTS_BANNER_IMAGE_ID, { width: 1200 })}) lightgray 75% 12% / auto 180% no-repeat;

  ${mediaQueryUp.tablet} {
    background-position: 70% 18%;
    background-size: auto 160%;
  }

  ${mediaQueryUp.desktop} {
    background-position: 80% 23%;
    background-size: auto 200%;
  }
`

// Same white ring overlay treatment as the LuxPlus+ Month campaign hero image
// (LuxPlusMonthTakeoverHero's HeroImageRing) — a blob shape with one sharp corner, centered
// horizontally in the image, sized to match the image exactly on mobile.
const ImageRing = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
    position: absolute;
    inset-inline-start: 50%;
    transform: translateX(-50%);
    bottom: ${rem(40)};
    width: ${rem(404.421)};
    height: ${rem(404.421)};
    border: ${rem(3.9395)} solid ${props => props.theme.palette.neutral.surface.staticWhite};
    border-start-start-radius: ${BLOB_SHAPE_LARGE_RADIUS_PERCENT};
    border-start-end-radius: ${BLOB_SHAPE_LARGE_RADIUS_PERCENT};
    border-end-end-radius: ${BLOB_SHAPE_SMALL_RADIUS_PERCENT};
    border-end-start-radius: ${BLOB_SHAPE_LARGE_RADIUS_PERCENT};
    pointer-events: none;

    &:dir(rtl) {
      transform: translateX(50%);
    }
  }
`

function LuxPlusLandingFlightsBanner() {
  const intl = useIntl()
  const imageAlt = intl.formatMessage({
    id: 'oXp2XEouRsAKGA9RPzk0',
    defaultMessage: 'View from an aeroplane window above the clouds',
    description: 'Alt text for the LuxPlus flights banner image shown on the LuxPlus landing page',
  })

  return (
    <BannerContainer variant="large" id="lux-plus-landing-flights-banner" data-testid="lux-plus-landing-flights-banner">
      <Banner background="lux-plus" paletteType="inverse">
        <BannerGrid>
          <TitleBlock>
            <Heading variant="heading2" align="start">
              <FormattedMessage
                id="ZLfFlNpqfg7tix2cabLo"
                defaultMessage="Bigger savings on flights"
                description="Heading for the flights pricing banner shown on the LuxPlus landing page"
              />
            </Heading>
            <BodyTextBlock variant="medium" align="start">
              <FormattedMessage
                id="8KGsWg6Fi08pKP45_QxR"
                defaultMessage="LuxPlus+ members save on flights to hundreds of destinations worldwide"
                description="Body copy explaining the flights pricing benefit on the LuxPlus landing page"
              />
            </BodyTextBlock>
            <ButtonBreakout>
              <Pane background="none" paletteType="default">
                <ProductPaletteProvider palette="lux-plus">
                  <TextButton
                    kind="primary"
                    variant="ghost"
                    size="medium"
                    shape="round"
                    fit="mobile-full-width"
                    to="/flights"
                  >
                    <FormattedMessage
                      id="Ix-9nqDjCjjQotU2Qbfr"
                      defaultMessage="Search flights"
                      description="Call to action button text for the LuxPlus landing flights banner linking to the flights vertical page"
                    />
                  </TextButton>
                </ProductPaletteProvider>
              </Pane>
            </ButtonBreakout>
          </TitleBlock>
          <ImageWrapper role="img" aria-label={imageAlt}>
            <ImageFrame />
            <ImageRing />
          </ImageWrapper>
        </BannerGrid>
      </Banner>
    </BannerContainer>
  )
}

export default LuxPlusLandingFlightsBanner
